import React, { useState, useCallback } from "react";
import { TreeNode } from "./types";
import "./treeModal.css";

type Props = {
  nodes: TreeNode[];
  toggleNode: (id: string) => void;
  getSelectionState: (id: string) => {
    checked: boolean;
    indeterminate: boolean;
  };
  externalExpanded?: Record<string, boolean>;
};

export function TreeView({
  nodes,
  toggleNode,
  getSelectionState,
  externalExpanded = {}
}: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpand = useCallback((id: string) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const renderNode = useCallback(
    (node: TreeNode, level: number = 0) => {
      const children = node.children ?? [];
      const hasChildren = children.length > 0;

      const isOpen =
        externalExpanded[node.id] ?? expanded[node.id];

      const state = getSelectionState(node.id);

      return (
        <div key={node.id} className="tm-node">
          <div className="tm-node-row" style={{ paddingLeft: level * 16 }}>

            {/* expand */}
            <div className="tm-node-expand">
              {hasChildren && (
                <button
                  className="tm-expand-btn"
                  onClick={() => toggleExpand(node.id)}
                >
                  {isOpen ? "▼" : "▶"}
                </button>
              )}
            </div>

            {/* checkbox */}
            <div
              className={[
                "tm-checkbox",
                state.checked ? "checked" : "",
                state.indeterminate ? "indeterminate" : ""
              ].join(" ")}
              onClick={() => toggleNode(node.id)}
            >
              {state.checked && !state.indeterminate && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}

              {!state.checked && state.indeterminate && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6 12h12"
                    stroke="#93c5fd"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </div>

            {/* label */}
            <div className="tm-label">
              {node.label}
            </div>
          </div>

          {/* children */}
          {hasChildren && isOpen && (
            <div className="tm-children">
              {children.map((child) =>
                renderNode(child, level + 1)
              )}
            </div>
          )}
        </div>
      );
    },
    [expanded, externalExpanded, getSelectionState, toggleNode, toggleExpand]
  );

  return (
    <div className="tm-tree">
      {nodes.map((n) => renderNode(n))}
    </div>
  );
}